import {ApiSortOrder, ApiSortsDerived} from '@/api/meta/odata';
import Followup, {ApiFollowup, FollowupFilters} from '@/model/Followup';
import {FilterDisplayItem, createVersionedStorage} from './types';
import {fulltextToDisplay} from '@/util/store';
import {providerStatusFilterData} from './providers';
import {getFollowups} from '@/api/module/followup';
import {ValueType} from '@/api/meta/filters';
import {useSettings} from './settings';
import pinia from '@/plugins/pinia';
import moment from 'moment';
import {formatDatum} from '@/model/ContactHistory';

// const filterNames: Record<keyof FollowupFilters, string | null> = {
//   'Fulltext$Fulltext': 'Suche',
//   'Erledigt': null,
//   'KundStatus$In': null,
//   'Bediener$Any': 'Bediener',
//   'AnlageDatum$From': 'Anlage-Datum von',
//   'AnlageDatum$To': 'Anlage-Datum bis',
// };

// const filterBedienerNames: Record<keyof FollowupFilters['Bediener$Any'], string | null> = {
//   'Bediener': 'Bediener',
//   'Erledigt/Status': null,
//   'WiedervorlageDatum$From': 'Wiedervorlagedatum von',
//   'WiedervorlageDatum$To': 'Wiedervorlagedatum bis',
// };

const useFollowupsStore = createVersionedStorage<
  FollowupFilters,
  ApiSortsDerived<ApiFollowup>,
  Followup[]
>('followups', {
  fetchFn: getFollowups,
  formatter: {
    Fulltext$Fulltext: fulltextToDisplay,
    AnlageDatum$From: (val) => 'Anlage-Datum von: ' + formatDatum(val!, '', false),
    AnlageDatum$To: (val) => 'Anlage-Datum bis: ' + formatDatum(val!, '', false),
    KundStatus$In(val) {
      if (val) {
        return val.map((prov, indx) => ({
          text: '',
          color: providerStatusFilterData[prov].color,
          keypath: [indx.toString()],
        }));
      }
    },
    Bediener$Any(val) {
      const result: FilterDisplayItem[] = [];
      if (val['Erledigt/Status'] !== undefined) {
        const stat = val['Erledigt/Status'];
        result.push( {
          text: stat ? 'erledigt' : 'offen',
          color: stat ? 'error' : 'success',
          keypath: ['Erledigt/Status'],
        });
      }
      if (val['Gelesen/Status'] !== undefined) {
        const stat = val['Gelesen/Status'];
        result.push( {
          text: stat ? 'gelesen' : 'ungelesen',
          keypath: ['Gelesen/Status'],
        });
      }
      if (val.Bediener !== undefined) {
        result.push({
          text: 'Bediener: ' + String(val.Bediener ?? useSettings(pinia).userShort),
          keypath: ['Bediener'],
        });
      }
      if (val.WiedervorlageDatum$From !== undefined) {
        result.push({
          text: 'Wiedervorlagedatum von: ' + formatDatum(val.WiedervorlageDatum$From, '', false),
          keypath: ['WiedervorlageDatum$From'],
        });
      }
      if (val.WiedervorlageDatum$To !== undefined) {
        result.push({
          text: 'Wiedervorlagedatum bis: ' + formatDatum(val.WiedervorlageDatum$To, '', false),
          keypath: ['WiedervorlageDatum$From'],
        });
      }
      return result;
    },
  },
  // convertFilterToDisplayFn: createConvertFiltersToDisplayFn(
  //     createGetRowFn({
  //       getLabel: ({actualKeys}) => {
  //         if (actualKeys[0] === 'Bediener$Any') {
  //           return filterBedienerNames[actualKeys[1] as keyof FollowupFilters['Bediener$Any']];
  //         }

  //         return filterNames[actualKeys[0] as keyof FollowupFilters];
  //       },
  //       getColor: ({actualKeys, value}) => {
  //         if (actualKeys[0] === 'KundStatus$In') return providerStatusFilterData[value as ProviderStatus].color;
  //         if (actualKeys[0] === 'Bediener$Any' && actualKeys[1] === 'Erledigt/Status') {
  //           return value === true || value === 'J' ? 'error' : 'success';
  //         }

  //         return undefined;
  //       },
  //       getValue: ({actualKeys, value}) => {
  //         if (actualKeys[0] === 'KundStatus$In') return providerStatusFilterData[value as ProviderStatus].value;
  //         if (actualKeys[0] === 'Bediener$Any') {
  //           switch (actualKeys[1] as keyof FollowupFilters['Bediener$Any']) {
  //             case 'Erledigt/Status':
  //               if (value === undefined) return null;
  //               return value === true || value === 'J' ? 'erledigt' : 'offen';
  //             case 'Bediener':
  //               // handled this way in request
  //               return String(value ?? useSettings(pinia).userShort);
  //           }
  //         }

  //         return undefined;
  //       },
  //     }),
  // ),
  defaultData: [],
  defaultFilters: {
    Fulltext$Fulltext: {
      words: [],
      fields: [
        {
          path: ['Bediener', 'Bediener'],
          valueType: ValueType.StringContains,
        },
        {
          path: ['Thema'],
          valueType: ValueType.StringContains,
        },
        {
          path: ['KundenNr'],
          valueType: ValueType.NumberStartsWith,
        },
        {
          path: ['KundeName'],
          valueType: ValueType.StringContains,
        },
      ],
    },
    Bediener$Any: {
      'Bediener': useSettings(pinia).userShort,
      'Erledigt/Status': false,
      'WiedervorlageDatum$From': moment().subtract(1, 'years').format('YYYY-MM-DD'),
      'WiedervorlageDatum$To': moment().format('YYYY-MM-DD'),
    },
  },
  defaultLimit: 10,
  defaultSorts: [['AnlageDatum', ApiSortOrder.Desc]],
});

export default useFollowupsStore;
